import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ExportImportButtons } from "@/components/ExportImportButtons";

const WARRANTY_TYPES = ["Comprehensive", "Compressor", "Motor", "Parts", "Service"];

export default function WarrantyMaster() {
  const [rules, setRules] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [r, b, p] = await Promise.all([
      api.get("/warranty/rules"),
      api.get("/masters/brands"),
      api.get("/products"),
    ]);
    setRules(r.data);
    setBrands(b.data);
    setProducts(p.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!confirm(`Delete warranty rule for ${r.brand || "*"}/${r.product_category || "*"}/${r.product_name || "*"}?`)) return;
    try {
      await api.delete(`/warranty/rules/${r.id}`);
      toast.success("Warranty rule deleted");
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-5" data-testid="warranty-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Warranty Master</h1>
          <p className="text-sm text-slate-500 mt-1">
            Define warranty coverage rules. The most specific match wins during complaint registration.
            {rules.length} rules.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <ExportImportButtons kind="warranty_rules" label="Warranty Rules" onDone={load} />
          <RuleDialog mode="create" brands={brands} products={products} onDone={load} />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && <div className="p-4 text-sm text-slate-500">Loading...</div>}
          <table className="w-full dense-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Brand</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Category</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Product / Model</th>
                <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Type</th>
                <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Comp.</th>
                <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Compressor</th>
                <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Motor</th>
                <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Parts</th>
                <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Service</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} data-testid={`warranty-row-${r.id}`}>
                  <td className="text-slate-800 font-medium">{r.brand || "*"}</td>
                  <td className="text-slate-700">{r.product_category || "*"}</td>
                  <td className="text-slate-700 text-xs">{r.product_name || "*"}{r.model_number && <span className="ml-1 font-mono text-slate-500">· {r.model_number}</span>}</td>
                  <td className="text-slate-700 text-xs">{r.warranty_type}</td>
                  <td className="text-right font-mono text-xs">{r.comprehensive_months ?? "—"}</td>
                  <td className="text-right font-mono text-xs">{r.compressor_months ?? "—"}</td>
                  <td className="text-right font-mono text-xs">{r.motor_months ?? "—"}</td>
                  <td className="text-right font-mono text-xs">{r.parts_months ?? "—"}</td>
                  <td className="text-right font-mono text-xs">{r.service_months ?? "—"}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      <RuleDialog mode="edit" rule={r} brands={brands} products={products} onDone={load} />
                      <Button variant="outline" size="sm" onClick={() => remove(r)} data-testid={`delete-warranty-${r.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


function RuleDialog({ mode, rule, brands, products, onDone }) {
  const isEdit = mode === "edit";
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    brand: "", product_category: "", product_name: "", model_number: "",
    warranty_type: "Comprehensive",
    comprehensive_months: "", motor_months: "", compressor_months: "", parts_months: "", service_months: "",
    remarks: "", active: true,
  });

  useEffect(() => {
    if (open && rule) setF({
      brand: rule.brand || "",
      product_category: rule.product_category || "",
      product_name: rule.product_name || "",
      model_number: rule.model_number || "",
      warranty_type: rule.warranty_type || "Comprehensive",
      comprehensive_months: rule.comprehensive_months ?? "",
      motor_months: rule.motor_months ?? "",
      compressor_months: rule.compressor_months ?? "",
      parts_months: rule.parts_months ?? "",
      service_months: rule.service_months ?? "",
      remarks: rule.remarks || "",
      active: rule.active ?? true,
    });
    if (open && !rule) setF({
      brand: "", product_category: "", product_name: "", model_number: "",
      warranty_type: "Comprehensive",
      comprehensive_months: "", motor_months: "", compressor_months: "", parts_months: "", service_months: "",
      remarks: "", active: true,
    });
  }, [open, rule]);

  const categories = Array.from(new Set(products.map((p) => p.product_category).filter(Boolean))).sort();

  const submit = async () => {
    const payload = {
      ...f,
      comprehensive_months: f.comprehensive_months === "" ? null : parseInt(f.comprehensive_months) || null,
      motor_months: f.motor_months === "" ? null : parseInt(f.motor_months) || null,
      compressor_months: f.compressor_months === "" ? null : parseInt(f.compressor_months) || null,
      parts_months: f.parts_months === "" ? null : parseInt(f.parts_months) || null,
      service_months: f.service_months === "" ? null : parseInt(f.service_months) || null,
    };
    try {
      if (isEdit) await api.put(`/warranty/rules/${rule.id}`, payload);
      else await api.post("/warranty/rules", payload);
      toast.success(isEdit ? "Rule updated" : "Rule created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const upd = (k, v) => setF({ ...f, [k]: v });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="outline" size="sm" data-testid={`edit-warranty-${rule.id}`}><Pencil size={12} /></Button>
          : <Button className="bg-slate-900 text-white" data-testid="new-warranty-btn"><Plus size={14} className="mr-1" /> New Warranty Rule</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? "Edit warranty rule" : "Create warranty rule"}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Leave a scope field blank to make the rule wildcard (e.g. blank Model = all models of that product).</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Brand</Label>
            <Select value={f.brand || "__any__"} onValueChange={(v) => upd("brand", v === "__any__" ? "" : v)}>
              <SelectTrigger data-testid="warranty-brand-select"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any brand</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Product Category</Label>
            <Select value={f.product_category || "__any__"} onValueChange={(v) => upd("product_category", v === "__any__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any category</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Product Name</Label><Input value={f.product_name} onChange={(e) => upd("product_name", e.target.value)} /></div>
          <div><Label>Model Number</Label><Input value={f.model_number} onChange={(e) => upd("model_number", e.target.value)} /></div>
          <div>
            <Label>Warranty Type</Label>
            <Select value={f.warranty_type} onValueChange={(v) => upd("warranty_type", v)}>
              <SelectTrigger data-testid="warranty-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{WARRANTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Checkbox checked={f.active} onCheckedChange={(v) => upd("active", !!v)} />
            <Label>Active</Label>
          </div>
          <div><Label>Comprehensive (months)</Label><Input type="number" value={f.comprehensive_months} onChange={(e) => upd("comprehensive_months", e.target.value)} data-testid="warranty-comprehensive-input" /></div>
          <div><Label>Compressor (months)</Label><Input type="number" value={f.compressor_months} onChange={(e) => upd("compressor_months", e.target.value)} /></div>
          <div><Label>Motor (months)</Label><Input type="number" value={f.motor_months} onChange={(e) => upd("motor_months", e.target.value)} /></div>
          <div><Label>Parts (months)</Label><Input type="number" value={f.parts_months} onChange={(e) => upd("parts_months", e.target.value)} /></div>
          <div><Label>Service (months)</Label><Input type="number" value={f.service_months} onChange={(e) => upd("service_months", e.target.value)} /></div>
          <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={f.remarks} onChange={(e) => upd("remarks", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-slate-900 text-white" data-testid="warranty-save-btn">{isEdit ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
